<template>
  <v-container
    class="my-4 fill-height"
    v-if="this.group.my_role == 'admin' || this.$auth.check('uber_admin')"
  >
    <v-flex v-if="!loading && group && group.id">
      <v-flex xs12 lg8>
        <h3 class="mb-3 pr-4 mr-4">Bulk Import Group Members: {{ group.name }}</h3>
        <hr class="thick mt-4 mb-2" />
        <p>This tool provides the ability to manage group membership in bulk. You can use a CSV file to specify which users are members of the group. If you encounter any issues while using this functionality, please let us know by emailing <a href="mailto:amaze@rti.org">amaze@rti.org</a>.</p>
        <v-expansion-panel v-model="instructions" class="mx-0 px-0">
        <v-expansion-panel-content>
          <template v-slot:header>
            <div class="px-2"><strong>How to import members</strong> (<small>Click to <span v-if="instructions === 0">hide</span><span v-else>view</span></small>)</div>
          </template>
          <v-card>
            <v-card-text><ul class="my-0 list">
            <li>Before you begin, <strong><a @click.prevent="downloadBackup()">download this backup</a></strong> of the current group members.</li>
            <li>
              <strong>Start</strong> by creating a CSV import file that contains the email addresses of <strong>users with existing accounts</strong>.
              <ul class="my-0 list">
                <li>You may <strong><a href="https://amaze-assets.s3.us-west-1.amazonaws.com/public/member.import.sample.csv" download="member.import.sample.csv" target="_blank">download this sample CSV file</a></strong>, fill in the email addresses, then use it for this import.</li>
                <li>This import <strong>does not create new user accounts</strong>. It will only add members to the group if they have an existing account.</li>
                <li>You will need to create accounts for any users who do not currently have one.</li>
              </ul>
            </li>
            <li>When you have a CSV file ready, click "<em>Choose File</em>" and select the CSV for upload.</li>
            <li>Select the settings and options relevant to your import.
              <ul class="my-0 list">
                <li>By default, the import process only adds members to the group, it does not remove members. If you want to do a complete refresh of the group members and <strong>remove all current members</strong> of the group before importing the new members, check "<em>Replace Current Members</em>".
                <li>If the first row of the CSV contains non-data column headers, please check the "<em>First Row Contains Headers</em>" option.</li>
              </ul>
            </li>
            <li>Carefully <strong>review the data</strong> and check for any warning or error messages.</li>
            <li>Once you are satisfied with the data, press "<em>Import Members</em>" to <strong>finalize the import</strong>.</li>
            <li>After completing an import, return to the group and use the default membership management functionality to make any additional adjustments to the memberships, such as adding administrators to the group.</li>
          </ul></v-card-text>
          </v-card>
        </v-expansion-panel-content>
        </v-expansion-panel>
      </v-flex>

      <v-flex xs12 lg8>
        <v-layout class="my-4" v-if="false">
        <p v-if="content">
          <h2>Content:</h2> {{ content }}
        </p>
      </v-layout>

      <v-layout class="my-4">
        <v-flex>
          <h3 class="my-2">1. Download a backup</h3>
          <p>You may optionally choose to download a backup of the current group memberships.</p>
          <v-btn @click.prevent="downloadBackup()" class="ml-1" color="secondary">Download Backup</v-btn>
        </v-flex>
      </v-layout>

      <v-layout class="my-4">
        <v-flex>
          <h3 class="my-2">2. Choose the CSV file</h3>
          <p>Add email addresses of existing users to <strong><a href="https://amaze-assets.s3.us-west-1.amazonaws.com/public/member.import.sample.csv" download target="_blank">this sample CSV file</a></strong>, then click the "Choose File" button below to upload it.</p>
          <v-btn @click="triggerUpload()" class="ml-1" color="primary"
          >Choose File</v-btn
        >
          <label v-if="file && file.name">{{ file.name }}</label>
        </v-flex>
        <input v-show="false" type="file" accept=".csv" ref="fileUploadButton" id="fileUploadButton" @change="handleFileUpload($event)" />
      </v-layout>

      <v-layout class="my-4">
        <v-flex  xs12>
          <h3 class="my-2">3. Select options and settings</h3>

          <div class="my-4">
            <v-checkbox class="my-0 py-0" v-model="replaceMembers" label="Replace Current Members"></v-checkbox>
            <p class="pa-0 mx-1"><small>If you choose to replace current members, all current members of this group will be removed and only the valid users specified in the upload file will be members.</small></p>
          </div>

          <div class="mt-4">
            <v-checkbox class="my-0 py-0" v-model="hasHeaders" label="First Row Contains Headers"></v-checkbox>
            <p class="pa-0 mx-1"><small>If the first row of the import file contains headers (ex: "Email Address"), check this box so we can understand the data format.</small></p>
          </div>
        </v-flex>
      </v-layout>

      <v-layout class="my-4">
        <v-flex>
          <h3 class="my-2">4. Review the data</h3>
          <p v-if="data" class="py-0 my-0">
            The following list of emails or user ids will become {{ this.role }}s of group {{  group.name }} (ID: {{  group.id  }}).
          </p>
          <v-data-table v-if="data" :items="data" :headers="[{text: 'ID or Email',  sortable: false}]" xs4 sm4>
            <template v-slot:items="props">
              <td>{{ props.item }}</td>
            </template>
          </v-data-table>
          <p v-else>The data will appear here when uploaded.</p>
        </v-flex>
      </v-layout>

      <v-layout>
        <v-flex>
          <h3 class="my-2">5. Run the import</h3>
          <p><strong>Please note:</strong> This tool does not create new user accounts. Any ids or emails that are not associated with an existing user will be ignored.</p>

          <v-btn
          class="ml-0"
          :disabled="!processed || !parsed || !data || data.length < 1"
          color="primary"
          @click.enter="saveGroupMembers"
          >Import Members</v-btn>
          <v-progress-circular
            v-if="submitting"
            indeterminate
            rounded
            size="22"
            color="primary"
            class="mx-2"
          ></v-progress-circular>
        </v-flex>
      </v-layout>

      <v-layout class="my-4">
        <v-flex>
          <h3 class="my-2">6. Review the feedback</h3>
          <v-data-table v-if="messages && !submitting" :items="messages" :headers="[{text: 'Message',  sortable: false}]" xs4 sm4>
            <template v-slot:items="props">
              <td>{{ props.item }}</td>
            </template>
          </v-data-table>
          <p v-else>Feedback, if any, will appear after members are imported.</p>
        </v-flex>
      </v-layout>

      <v-layout class="my-4">
        <v-flex xs8>
          <v-btn @click="returnToGroup()" class="ml-1" flat outline
            >Return to the Group</v-btn
          >
        </v-flex>
      </v-layout>
      </v-flex>

    </v-flex>
    <v-flex class="px-0 py-4 my-4 fill-height" v-if="loading">
      <h3 class="mb-3">
        Locating this group.
        <v-progress-circular
        indeterminate
        :size="20"
        :width="2"
        class="mx-2"
        color="primary"
      ></v-progress-circular>
    </h3>
    </v-flex>
    <v-container class="pa-0 fill-height" v-if="!loading && (!group || !group.id)">
      <h3 class="mb-3">Unable to find this group.</h3>
    </v-container>
  </v-container>
  <v-container class="pa-0" v-else>
    <h3 class="mb-3" v-if="this.group.my_role">
      You do not have access to this feature.
    </h3>
  </v-container>
</template>
<style lang="scss" scoped>
@import "src/styles/variables.scss";

::v-deep  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
  margin-bottom: 0;
}
</style>

<script>
import GroupService from "@/services/GroupService";
import VuePapaParse from 'vue-papa-parse';
import Vue from 'vue';
Vue.use(VuePapaParse);

export default {
  data() {
    return {
      loadingGroup: false,
      loadingMembers: false,
      instructions: false,
      group: {},
      members: null,
      role: 'member',
      dirty: false,
      errors: false,
      file: null,
      content: null,
      data: null,
      parsed: false,
      processed: false,
      submitting: false,
      replaceMembers: false,
      hasHeaders: false,
      messages: null,
      backup: null
    };
  },
  computed: {
    group_id() {
      return this.$route.params.id;
    },
    backupFileName() {
      const today = new Date();
      const date = today.toISOString().substring(0, 10);
      return date + ' ' + this.group.name + ' members backup' + '.csv';
    },
    loading() {
      return this.loadingGroup || this.loadingMembers;
    }
  },
  watch: {
    file(val) {
      this.parseFile();
    },
    hasHeaders(val) {
      this.parseFile();
    },
    content(val) {
      if (val) {
        this.processFileContent();
      }
    }
  },
  methods: {
    initImport() {
      this.reset();
      this.loadingGroup = false;
      this.loadingMembers = false;
      this.role = 'member';
      this.dirty = false;
      this.errors = false;
      this.replaceMembers = false;
      this.hasHeaders = false;
      this.messages = null;
      this.members = null;
      this.backup = null;

      const returnRoute = "/group/" + this.$route.params.id;
      this.$emit('setReturnRoute', returnRoute);
      this.retrieveGroup(this.$route.params.id);
    },
    reset: function() {
      // Reset the things.
      this.content = null;
      this.data = null;
      this.parsed = false;
      this.processed = false;
      this.submitting = false;
      this.file = null;
      this.backup = null;
    },
    triggerUpload: function() {
      this.$refs.fileUploadButton.click();
    },
    handleFileUpload: function(event) {
      this.reset();
      this.warnings = null;
      try {
        this.file = event.target.files[0];
      } catch (e) {
        // I hope catching the exception will prevent the page from breaking completely.
        // Let's add an error message.
      }
    },
    parseFile: function() {
      if (this.file) {
        this.$papa.parse(this.file, {
          header: this.hasHeaders,
          skipEmptyLines: true,
          complete: function (results) {
            this.content = results;
            this.parsed = true;
          }.bind(this)
        });
      }
      // Maybe do something to show an error message if the file isn't uploaded yet.
    },
    processFileContent: function() {
      if (!(this.parsed && this.validateData())) {
        // Maybe do something to show error messaging if there is no data.
        return;
      }

      // Data will be an array.
      this.data = [];

      // ValidateData above should cover us here.
      for (const key in this.content.data) {
        let row = this.content.data[key];
        let data = this.hasHeaders ? row[Object.keys(row)[0]] : row[0];

        if (data) {
          this.data.push(data);
        } else {
          // Probably want a warning / error here as well.
        }
      }

      this.processed = true;
    },
    validateData() {
      if (this.parsed === true && this.content && this.content.data) {
        return true;
      }
      // You got here, something's wrong.
      return false;
    },
    returnToGroup() {
      if (this.group.id) {
        this.$router.push("/group/" + this.group.id);
      } else {
        this.$router.go(-1);
      }
    },
    saveGroupMembers() {
      // Make sure this.group.id is set.
      // Make sure that role is set.
      // Make sure this.data is set and has members.
      if (
        this.group && this.group.id && this.group.id > 0 &&
        this.role &&
        this.data && this.data.length > 0
      ) {
        var formData = {
          "group_id": this.group.id,
          "role_id": this.role,
          "user_ids": this.data,
          "replace": !!this.replaceMembers
        };
      }

      if (!formData) {
        this.$store.commit("updateMessage", {
          message: "We have encountered a problem updating the members of this group.",
          snackbar: true
        });
        return false;
      }

      // Do some final validation / verification here.
      this.messages = [];
      // This is a shortcut to disable the import button after submitting.
      this.parsed = false;
      this.submitting = true;

      this.$http.post("memberships/import", formData).then(
        response => {
          try {
            this.messages = [];
            const status = (response.body.data.foundMembers ? response.body.data.foundMembers : 0) +
            " of " + formData.user_ids.length +
            " members added to group.";
            this.messages.push(status);
            this.warnings = response.body.data.warnings;
            for (const key in response.body.data.warnings) {
              let row = "WARNING: " + response.body.data.warnings[key];
              this.messages.push(row);
            }
          } catch (e) {
            this.messages.push('An error was encountered after members were imported. Please check the group to verify memberships are correct.');
          }
          this.$store.commit("updateMessage", {
            message: "Members saved.",
            snackbar: true
          });
          this.submitting = false;
        },
        response => {
          console.log("Error saving members.");
          this.submitting = false;
        }
      );
    },
    retrieveGroup(groupId) {
      this.loadingGroup = true;
      this.$http
        .get("groups/" + groupId)
        .then(
          response => {
            this.group = response.body.data;
            this.retrieveGroupMembers(groupId);
          },
          response => {
            console.log("Error getting group data.");
          }
        )
        .finally(response => {
          this.loadingGroup = false;
        });
    },
    retrieveGroupMembers(groupId) {
      this.loadingMembers = true;
      this.$http
        .get("groups/members/" + groupId)
        .then(
          response => {
            this.members = response.body.data;
          },
          response => {
            console.log("Error getting group members.");
          }
        )
        .finally(response => {
          this.loadingMembers = false;
        });
    },
    generateBackup() {
      // We only need the member id.
      // Members needs to be loaded before calling this function.
      const memberIds = this.members.map((member, index) => {
        return { id: member.id };
      });

      const csv = this.$papa.unparse(memberIds);
      const data = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      this.backup = null;

      if (navigator.msSaveBlob) {
        this.backup = navigator.msSaveBlob(data, this.backupFileName);
      } else {
        this.backup = window.URL.createObjectURL(data);
      }
    },
    downloadBackup() {
      // Generate the backup data.
      if (!this.backup) {
        this.generateBackup();
      }

      const tempLink = document.createElement('a');
      tempLink.href = this.backup;
      tempLink.setAttribute('download', this.backupFileName);
      tempLink.click();
    }
  },
  activated () {
    this.initImport();
  },
  mounted() {
    this.initImport();
  }
};
</script>
